"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { BuilderUnsavedChangesDialog } from "@/app/components/BuilderUnsavedChangesDialog";

type Confirmation = {
  message?: string;
  resolve: (confirmed: boolean) => void;
};

type DraftGuard = {
  dirty: boolean;
  navigation: boolean;
  confirm: () => Promise<boolean>;
};

const draftGuards = new Set<DraftGuard>();
let navigationGuardCount = 0;
let releaseNavigationListeners: (() => void) | null = null;
let allowedUnload = false;
let allowedUnloadTimeout: ReturnType<typeof setTimeout> | undefined;

function hasUnsavedChanges(): boolean {
  return [...draftGuards].some((guard) => guard.dirty);
}

export function requestDocumentNavigation(): Promise<boolean> {
  if (!hasUnsavedChanges()) return Promise.resolve(true);
  const guards = [...draftGuards];
  const owner = guards.find((guard) => guard.navigation && guard.dirty)
    ?? guards.find((guard) => guard.navigation);
  return owner?.confirm() ?? Promise.resolve(false);
}

/** Used when changing an editor view would unmount a local translation draft. */
export function requestBuilderPanelNavigation(): Promise<boolean> {
  const dirtyPanel = [...draftGuards].find((guard) => !guard.navigation && guard.dirty);
  return dirtyPanel?.confirm() ?? Promise.resolve(true);
}

function allowNextDocumentNavigation(): void {
  allowedUnload = true;
  clearTimeout(allowedUnloadTimeout);
  // A cancelled or client-side navigation must not disable future protection.
  allowedUnloadTimeout = setTimeout(() => { allowedUnload = false; }, 1_000);
}

type IndexedNavigationWindow = Window & {
  navigation?: { currentEntry?: { index: number } };
};

/** Shared by the document draft and its independently saved translation panels. */
export function useBuilderUnsavedChanges({
  dirty,
  navigation = false,
}: {
  dirty: boolean;
  navigation?: boolean;
}) {
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const confirmationRef = useRef<Confirmation | null>(null);
  const guardRef = useRef<DraftGuard>({ dirty, navigation, confirm: async () => true });

  const confirmDiscard = useCallback((message?: string): Promise<boolean> => {
    // Keep the first requested destination while its confirmation is open.
    if (confirmationRef.current) return Promise.resolve(false);

    return new Promise<boolean>((resolve) => {
      const next = { message, resolve };
      confirmationRef.current = next;
      setConfirmation(next);
    });
  }, []);

  const finishConfirmation = useCallback((confirmed: boolean) => {
    const pending = confirmationRef.current;
    confirmationRef.current = null;
    setConfirmation(null);
    pending?.resolve(confirmed);
  }, []);

  guardRef.current.dirty = dirty;
  guardRef.current.navigation = navigation;
  guardRef.current.confirm = confirmDiscard;

  const requestNavigation = useCallback((): Promise<boolean> => (
    hasUnsavedChanges() ? confirmDiscard() : Promise.resolve(true)
  ), [confirmDiscard]);

  useEffect(() => {
    const guard = guardRef.current;
    draftGuards.add(guard);
    return () => {
      draftGuards.delete(guard);
      confirmationRef.current?.resolve(false);
      confirmationRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!navigation) return;

    navigationGuardCount += 1;
    const release = () => {
      navigationGuardCount -= 1;
      if (navigationGuardCount === 0) {
        releaseNavigationListeners?.();
        releaseNavigationListeners = null;
      }
    };
    // A pending web page is preloaded beside the current page. Share one set
    // of browser listeners until the final editor session is unmounted.
    if (releaseNavigationListeners) return release;

    let replayedLink: HTMLAnchorElement | null = null;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (allowedUnload) {
        allowedUnload = false;
        return;
      }
      if (!hasUnsavedChanges()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const click = (event: MouseEvent) => {
      if (
        event.defaultPrevented || event.button !== 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        || !(event.target instanceof Element)
      ) return;

      const link = event.target.closest<HTMLAnchorElement>("a[href]");
      if (
        !link || link === replayedLink || link.hasAttribute("download")
        || (link.target && link.target !== "_self")
        || link.closest("[data-builder-canvas], [data-shop-canvas], [data-shop-builder-canvas-content], [data-modal-dialog]")
      ) return;

      const destination = new URL(link.href, window.location.href);
      if (
        !["http:", "https:"].includes(destination.protocol)
        || (destination.origin === window.location.origin
          && destination.pathname === window.location.pathname
          && destination.search === window.location.search)
        || !hasUnsavedChanges()
      ) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void requestDocumentNavigation().then((confirmed) => {
        if (!confirmed) return;
        allowNextDocumentNavigation();
        if (!link.isConnected) {
          window.location.assign(destination.href);
          return;
        }
        replayedLink = link;
        try {
          // Replay the original link so Next.js, shell links and their handlers
          // retain their exact navigation semantics.
          link.click();
        } finally {
          replayedLink = null;
        }
      });
    };

    // Track history positions without inserting duplicate editor entries.
    // A cancelled Back/Forward restores the original entry before opening the
    // dialog, and stops Next's popstate handler from unmounting the draft.
    const stateKey = "__builderDraftHistoryIndex";
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    const navigationWindow = window as IndexedNavigationWindow;
    const historyIndex = () => navigationWindow.navigation?.currentEntry?.index
      ?? (window.history.state?.[stateKey] as number | undefined);
    let currentIndex = historyIndex() ?? 0;
    let currentUrl = new URL(window.location.href);
    let restoring: (() => void) | null = null;
    let allowedTraversal = false;
    const pushState: History["pushState"] = function (this: History, data, unused, url) {
      originalPush.call(this, { ...data, [stateKey]: currentIndex + 1 }, unused, url);
      currentIndex = historyIndex() ?? currentIndex + 1;
      currentUrl = new URL(window.location.href);
      allowedUnload = false;
    };
    const replaceState: History["replaceState"] = function (this: History, data, unused, url) {
      originalReplace.call(this, { ...data, [stateKey]: currentIndex }, unused, url);
      currentUrl = new URL(window.location.href);
    };
    originalReplace.call(window.history, { ...window.history.state, [stateKey]: currentIndex }, "");
    window.history.pushState = pushState;
    window.history.replaceState = replaceState;

    const popState = (event: PopStateEvent) => {
      const destinationIndex = historyIndex() ?? currentIndex - 1;
      const destinationUrl = new URL(window.location.href);
      if (restoring) {
        event.stopImmediatePropagation();
        const restored = restoring;
        restoring = null;
        restored();
        return;
      }
      const sameDocumentLocation = destinationUrl.pathname === currentUrl.pathname
        && destinationUrl.search === currentUrl.search;
      if (allowedTraversal || !hasUnsavedChanges() || destinationIndex === currentIndex || sameDocumentLocation) {
        allowedTraversal = false;
        currentIndex = destinationIndex;
        currentUrl = destinationUrl;
        return;
      }
      event.stopImmediatePropagation();
      const distance = currentIndex - destinationIndex;
      restoring = () => {
        void requestDocumentNavigation().then((confirmed) => {
          if (!confirmed) return;
          allowedTraversal = true;
          allowNextDocumentNavigation();
          window.history.go(-distance);
        });
      };
      window.history.go(distance);
    };

    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", click, true);
    window.addEventListener("popstate", popState, true);
    releaseNavigationListeners = () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", click, true);
      window.removeEventListener("popstate", popState, true);
      if (window.history.pushState === pushState) window.history.pushState = originalPush;
      if (window.history.replaceState === replaceState) window.history.replaceState = originalReplace;
    };
    return release;
  }, [navigation]);

  return {
    confirmDiscard,
    requestNavigation,
    confirmationDialog: (
      <BuilderUnsavedChangesDialog
        open={confirmation !== null}
        message={confirmation?.message}
        onClose={() => finishConfirmation(false)}
        onConfirm={() => finishConfirmation(true)}
      />
    ),
  };
}
